import { Link, NavLink, useLocation, useSearchParams } from 'react-router-dom'
import { cn } from '@/components/ui/cn'
import { LogoutButton } from '@/components/auth/LogoutButton'
import { AdminNavLink } from '@/components/layout/AdminNavLink'
import { BrandMark } from '@/components/layout/BrandMark'
import { useMinCopy } from '@/i18n/useUiLocale'

export function Sidebar() {
  const { nav } = useMinCopy()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const feedbackActive =
    location.pathname === '/settings' && searchParams.get('tab') === 'feedback'

  const navItems = [
    { to: '/dashboard', label: nav.today, icon: '🏠' },
    { to: '/vocabulary', label: nav.words, icon: '📚' },
    { to: '/catalog', label: nav.catalog, icon: '🧭' },
    { to: '/reading', label: nav.reading, icon: '📖' },
    { to: '/review', label: nav.refresh, icon: '🔄' },
    { to: '/tests', label: nav.challenge, icon: '✏️' },
    { to: '/progress', label: nav.progress, icon: '📊' },
    { to: '/ai-practice', label: nav.practice, icon: '✨' },
    { to: '/settings', label: nav.settings, icon: '⚙️' },
  ]

  return (
    <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-card md:flex">
      <div className="border-b border-border p-6">
        <Link
          to="/dashboard"
          className="block rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="sidebar-brand-home"
          aria-label={nav.today}
        >
          <BrandMark showTagline />
        </Link>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {navItems.map(({ to, label, icon }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={to === '/catalog' ? 'sidebar-nav-catalog' : undefined}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                isActive
                  ? 'bg-primary/10 font-medium text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )
            }
          >
            <span>{icon}</span>
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="space-y-1 border-t border-border p-3">
        <NavLink
          to="/settings?tab=feedback"
          data-testid="sidebar-nav-feedback"
          className={cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
            feedbackActive
              ? 'bg-primary/10 font-medium text-primary'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          )}
        >
          <span>💬</span>
          {nav.feedback}
        </NavLink>
        <NavLink
          to="/faq"
          data-testid="sidebar-nav-faq"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
              isActive
                ? 'bg-primary/10 font-medium text-primary'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            )
          }
        >
          <span>❓</span>
          {nav.faq}
        </NavLink>
        <AdminNavLink />
        <LogoutButton fullWidth className="justify-start px-3" />
      </div>
    </aside>
  )
}
